import { makeAutoObservable } from "mobx";
import RootStore  from "mobx-stores/root-store";
import { Api } from "services";
import _ from "lodash";
import { CredicCardsDTO } from "models";
import {
  addToCartQuery,
  createShopifyUrl,
  getShopifyCredentials,
  getShopifyVariantId,
  initializeCartQuery,
  SHOPIFY_API_VERSION,
} from "utilities";

export class CartCheckoutStore {
  private rootStore: any;
  private userApi: Api["userApi"];
  private storeApi: Api["storeApi"];
  private api: Api;

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.api = this.rootStore.generalStore.api;
    this.userApi = this.rootStore.generalStore.api.userApi;
    this.storeApi = this.rootStore.generalStore.api.storeApi;
    makeAutoObservable(this);
  }

  // OBSERVABLES
  private storesProducts = null; // TODO-A Add model here
  private storeProductsCheckout = null;
  private paymentDetailsSelected = null;
  private newCreditCard = null;
  private saveNewCard: boolean = false;
  public creditCardFormEnabled: boolean = false;
  public isNewCard: boolean = false;

  // ACTIONS
  public changeCollpaseStateCheckoutStore = (storeName: string): void => {
    this.storeProductsCheckout[storeName].collapsed =
      !this.storeProductsCheckout[storeName].collapsed;
  };

  public setNewCreditCardDetails = (values: CredicCardsDTO): void => {
    const cardType = values.number.charAt(0) === '4' ? 1 : 2;
    this.newCreditCard = {...values, cardType };
  };

  public setCreditCardFormEnabled = (isEnabled: boolean): void => {
    this.creditCardFormEnabled = isEnabled;
  };

  public setIsNewCard = (isNew: boolean): void => {
    this.isNewCard = isNew;
  };

  public switchSaveNewCard = () => {
    this.saveNewCard = !this.saveNewCard;
  };

  public selectPaymentDetails = (id: number) => {
    this.paymentDetailsSelected = id;
  };

  private setCartAndCheckoutStore = (stores): void => {
    this.storesProducts = stores;
    // Checkout page collapse menu and items info
    let storeProducts = _.cloneDeep(stores);
    _.forOwn(this.storesProducts, (store, keyStore) => {
      storeProducts[keyStore] = [
        ...storeProducts[keyStore].filter((product) => product.checked),
      ];
    });
    _.forOwn(storeProducts, (store, keyStore) => {
      if (!store.length) {
        delete storeProducts[keyStore];
      } else {
        storeProducts[keyStore] = {
          storeProducts: storeProducts[keyStore],
          products: storeProducts[keyStore].length,
          items: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return previousValue + currentValue.count;
            },
            0
          ),
          subtotal: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return previousValue + currentValue.price * currentValue.count;
            },
            0
          ),
          shippingfee: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return (
                previousValue + currentValue.shipping.price * currentValue.count
              );
            },
            0
          ),
          storeId: storeProducts[keyStore][0].product.storeId,
        };
        storeProducts[keyStore].total =
          storeProducts[keyStore].subtotal +
          storeProducts[keyStore].shippingfee;
        if (!this.storeProductsCheckout) {
          storeProducts[keyStore].collapsed = false;
        }
        else {
          if(this.storeProductsCheckout[keyStore]?.hasOwnProperty('collapsed')) {
            storeProducts[keyStore].collapsed = _.cloneDeep(this.storeProductsCheckout[keyStore].collapsed)
          } else {
            storeProducts[keyStore].collapsed = false;
          }
        }
      }
    });
    this.storeProductsCheckout = storeProducts;
  };

  // COMPUTED
  public get newCreditCardDetails(): CredicCardsDTO {
    return this.newCreditCard;
  }

  public get isNewCardPaymentOptionSelected(): boolean {
    return this.newCreditCard?.id === "";
  }

  public get isSaved(): boolean {
    return this.saveNewCard;
  }

  public get checkedCheckoutProducts(): any[] {
    const products = [];
    _.forOwn(this.storeProductsCheckout, (store) => {
      if (store?.storeProducts?.length) {
        products.push(...store.storeProducts);
      }
    });
    return products;
  }

  public get hasShopifyProduct(): boolean {
    return this.checkedCheckoutProducts.some(
      (p) => p?.product?.shopifyId != null
    );
  }

  public get hasNonShopifyProduct(): boolean {
    return this.checkedCheckoutProducts.some(
      (p) => p?.product?.shopifyId == null
    );
  }

  public get hasShopifyProductWithSameShopifyId(): boolean {
    const products = this.checkedCheckoutProducts;
    if (!products.length) return false;
    const firstProduct = products[0]?.product?.shopifyId;
    return products.every((p) => p?.product?.shopifyId === firstProduct);
  }

  public get isAllProductValidForShopifyCheckout(): boolean {
    if (!this.checkedCheckoutProducts.length) {
      // Cart page uses checked items from storesProducts directly
      const cartChecked = this.getAllCheckedCartProducts();
      if (!cartChecked.length) return true;

      const hasShopify = cartChecked.some((p) => p?.product?.shopifyId != null);
      const hasNonShopify = cartChecked.some((p) => p?.product?.shopifyId == null);

      if (hasNonShopify) {
        return !hasShopify;
      }
      if (hasShopify) {
        const firstProduct = cartChecked[0]?.product?.shopifyId;
        return cartChecked.every((p) => p?.product?.shopifyId === firstProduct);
      }
      return false;
    }

    if (this.hasNonShopifyProduct) {
      return !this.hasShopifyProduct;
    }
    if (this.hasShopifyProduct) {
      return this.hasShopifyProductWithSameShopifyId;
    }
    return false;
  }

  public get isShopifyOnlyCheckout(): boolean {
    return (
      this.hasShopifyProduct &&
      !this.hasNonShopifyProduct &&
      this.hasShopifyProductWithSameShopifyId
    );
  }

  public get confirmAndPayRestrict(): boolean {
    const { userAddressesInfo } = this.rootStore.accountStore;
    const hasAddress = Boolean(userAddressesInfo?.length);

    // Shopify checkout: payment happens on Shopify store
    if (this.isShopifyOnlyCheckout) {
      return hasAddress;
    }

    if (
      this.newCreditCard?.number?.length &&
      this.newCreditCard?.name?.length &&
      this.newCreditCard?.scv?.length &&
      this.newCreditCard?.expDate?.length
    ) {
      if (hasAddress) {
        return true;
      }
    }

    return Boolean(this.paymentDetailsSelected) && hasAddress;
  }

  public get cartStoreProducts() {
    // TODO-A Add model here
    return this.storesProducts;
  }

  public get cartStoreSelectedProducts() {
    return this.storeProductsCheckout;
  }

  public get orderSummary() {
    let orderSummary = {
      items: 0,
      shippingFee: 0,
      itemsTotal: 0,
      totalToPay: 0,
    };
    
    for (const keyStore in this.storesProducts) {
      const store = this.storesProducts[keyStore];
  
      for (const keyProduct in store) {
        const product = store[keyProduct];
  
        if (product.checked) {
          orderSummary.items += product.count;
          orderSummary.itemsTotal += product.price * product.count;
          orderSummary.shippingFee += product.shipping.price * product.count;
        }
      }
    }
  
    orderSummary.totalToPay = orderSummary.shippingFee + orderSummary.itemsTotal;
    return orderSummary;
  }

  public get cartStoreProductsSize() {
    // TODO-A Add model here
    let size = 0;
    for (let objStore in this.storesProducts) {
      size = size + _.size(this.storesProducts[objStore]);
    }
    return size;
  }

  private getAllCheckedCartProducts = (): any[] => {
    const products = [];
    _.forOwn(this.storesProducts, (store) => {
      if (Array.isArray(store)) {
        products.push(...store.filter((p) => p.checked));
      }
    });
    return products;
  };

  // METHODS
  public getCartByUserId = async (userId: number | string): Promise<any> => {
    if(userId) {
    const cartDetails = await this.userApi.getCartByUserId(userId);
    if (!cartDetails) return;
    this.setCartAndCheckoutStore(cartDetails);
    }
  };

  public deleteCartByProductId = async (
    userId: number | string,
    productId: string | number
  ): Promise<any> => {
    const cartDetails = await this.userApi.deleteCartByProductId(
      userId,
      productId
    );
    if (!cartDetails) return;
    this.getCartByUserId(userId);
  };

  public updateCartByProductId = async (
    userId: number | string,
    productDetails: any
  ): Promise<any> => {
    const payloadCart = {
      product: {
        id: productDetails?.product?.id,
      },
      sku: {
        id: productDetails?.sku?.id,
      },
      count: productDetails?.count,
    };

    try {
      const cartDetails = await this.userApi.updateCartByProductId(
        userId,
        productDetails?.id,
        payloadCart
      );
      if (!cartDetails) return;
      this.getCartByUserId(userId);
    } catch (e) {
      console.error(e);
    }
  };

  public checkCartProduct = async (
    userId: number | string,
    productDetails: any
  ): Promise<any> => {
    const checkPayload = {
      ids: [productDetails?.id],
      checked: productDetails.checked,
    };
    const productIdChecked = await this.userApi.setUserCartChecked(
      userId,
      checkPayload
    );
    if (!productIdChecked) return;
    this.getCartByUserId(userId);
  };

  public checkAllProductsByStore = async (
    userId: number | string,
    storeName: string
  ): Promise<any> => {
    let checkedIds = null;
    let checked = null;
    if (
      this.storesProducts[storeName]
        .map((product) => product.checked)
        .every((checkedValue) => checkedValue)
    ) {
      checkedIds = this.setAllCheckedValues(storeName, false);
      checked = false;
    } else {
      checkedIds = this.setAllCheckedValues(storeName, true);
      checked = true;
    }
    const checkPayload = {
      ids: checkedIds,
      checked: checked,
    };
    const productIdChecked = await this.userApi.setUserCartChecked(
      userId,
      checkPayload
    );
    if (!productIdChecked) return;
    this.getCartByUserId(userId);
  };

  public initializeCartShopify = async (
    orderProducts: any[],
    attributes: Array<{ attribute: { orders: object } }>
  ): Promise<any> => {
    const [{ attribute: { orders } }] = attributes;
    if (!orderProducts.length) return null;

    const { shopifyStoreUrl, shopifyStorefrontToken } = getShopifyCredentials(
      orderProducts[0]
    );
    if (!shopifyStoreUrl || !shopifyStorefrontToken) {
      return null;
    }

    try {
      return await this.api.fetchShopify(
        createShopifyUrl(shopifyStoreUrl, SHOPIFY_API_VERSION),
        "post",
        initializeCartQuery([
          {
            key: "customer_source",
            value: "created_from_touche_marketplace",
          },
          {
            key: "orders",
            value: JSON.stringify({ ...orders }),
          },
        ]),
        shopifyStorefrontToken
      );
    } catch (error) {
      return error;
    }
  };

  public addToCartShopify = async (
    orderProducts: any[],
    payload: {
      lines: Array<{ merchandiseId: string; quantity: number }>;
      cartId: string;
    }
  ): Promise<any> => {
    if (!orderProducts.length) return null;

    const { shopifyStoreUrl, shopifyStorefrontToken } = getShopifyCredentials(
      orderProducts[0]
    );
    if (!shopifyStoreUrl || !shopifyStorefrontToken) {
      return null;
    }

    try {
      return await this.api.fetchShopify(
        createShopifyUrl(shopifyStoreUrl, SHOPIFY_API_VERSION),
        "post",
        addToCartQuery(payload),
        shopifyStorefrontToken
      );
    } catch (error) {
      return error;
    }
  };

  private openShopifyCheckoutWindow = (checkoutUrl: string): void => {
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      checkoutUrl,
      "_blank",
      `width=${width},height=${height},top=${top},left=${left},resizable=no,toolbar=no,menubar=no,scrollbars=no,status=no`
    );
  };

  private confirmAndPayShopify = async (
    userId: number | string,
    checkoutProducts: any[],
    checkoutAddress: any,
    accountDetails: any
  ): Promise<void> => {
    const { showError, showSuccess } = this.rootStore.generalStore;
    const { push } = this.rootStore.routingStore;

    if (checkoutProducts.some((p) => !getShopifyVariantId(p))) {
      showError("Selected product doesn't have variantId. Please try again.");
      return;
    }

    const { shopifyStoreUrl, shopifyStorefrontToken } = getShopifyCredentials(
      checkoutProducts[0]
    );
    if (!shopifyStoreUrl || !shopifyStorefrontToken) {
      showError("Shopify store credentials not found. Please try again.");
      return;
    }

    const orders = _.map(this.storeProductsCheckout, (store) => {
      return {
        shop: store.storeId,
        price: store.total,
        products: store.storeProducts.map((p) => ({
          ...p,
          shippingAddress: checkoutAddress[0].shippingAddress,
          billingAddress: checkoutAddress[0].billingAddress,
        })),
        status: "new",
        date: new Date().toISOString(),
        id: null,
      };
    });

    const attribute = {
      orders,
      user: JSON.parse(
        JSON.stringify({
          firstname: accountDetails?.firstName,
          lastname: accountDetails?.lastName,
          email: accountDetails?.emailAddress,
          phone: accountDetails?.phoneNumber,
          gender: accountDetails?.sex,
          birth: accountDetails?.dateOfBirth,
          id: accountDetails?.id,
        })
      ),
      userId: JSON.stringify(userId),
    };

    const initializeCartData = await this.initializeCartShopify(checkoutProducts, [
      { attribute: { orders: attribute } },
    ]);

    if (!initializeCartData?.data?.cartCreate?.cart?.id) {
      showError("Unable to initialize cart. Please try again.");
      return;
    }

    const cartId = initializeCartData.data.cartCreate.cart.id;
    const checkoutUrl = initializeCartData.data.cartCreate.cart.checkoutUrl;
    const linesItems = checkoutProducts.map((cp) => ({
      merchandiseId: `gid://shopify/ProductVariant/${getShopifyVariantId(cp)}`,
      quantity: cp.count,
    }));

    const isPromiseFulfilled = await this.addToCartShopify(checkoutProducts, {
      cartId,
      lines: linesItems,
    });

    if (!isPromiseFulfilled) {
      showError("Unable to add product to cart. Please try again.");
      return;
    }

    if (!checkoutUrl) {
      showError("Unable to get Shopify checkout URL. Please try again.");
      return;
    }

    const confirmed = await this.rootStore.generalStore.openShopifyPinDialog(
      "This is not a demo, purchases are real please take caution. You need a PIN code in order to complete purchase and you will be redirected to our shopify store where you can do payment."
    );

    if (!confirmed) return;

    this.openShopifyCheckoutWindow(checkoutUrl);

    for (const cp of checkoutProducts) {
      await this.deleteCartByProductId(userId, cp.id);
    }

    showSuccess("Redirected to Shopify store for payment");
    push("/stores");
  };

  public confirmAndPay = async (userId: number | string): Promise<any> => {
    const {
        routingStore: { push },
        accountStore: { userAddressesInfo, accountDetails },
      } = this.rootStore,
      primaryAddress = userAddressesInfo.filter((address) => address.primary),
      notPrimary = userAddressesInfo.filter((address) => !address.primary),
      checkoutAddress = !!primaryAddress.length ? primaryAddress : notPrimary;

    const { showSuccess, showError } = this.rootStore.generalStore;

    if (!checkoutAddress?.length) {
      showError(
        "No shipping address is provided. Go to My Profile > Addresses to add one."
      );
      return;
    }

    if (
      !accountDetails?.firstName &&
      !accountDetails?.lastName &&
      !accountDetails?.phoneNumber
    ) {
      showError(
        "User details are not provided. Go to My Profile > Account details to add them."
      );
      return;
    }

    const checkoutProducts = this.checkedCheckoutProducts;

    if (
      this.isShopifyOnlyCheckout &&
      this.hasShopifyProductWithSameShopifyId
    ) {
      await this.confirmAndPayShopify(
        userId,
        checkoutProducts,
        checkoutAddress,
        accountDetails
      );
      return;
    }

    const dateToIsoString = (date: string) => {
      let mm = date.slice(0, 2);
      let yy = date.slice(2, 4);
      let expDate = new Date(`${mm}/10/20${yy}`);
      return expDate?.toISOString();
    };

    let checkoutOrderPayload = {
      orders: [],
      user: {
        id: accountDetails?.id,
        firstname: accountDetails?.firstName,
        lastname: accountDetails?.lastName,
        email: accountDetails?.emailAddress,
        phone: accountDetails?.phoneNumber,
        gender: accountDetails?.sex,
        birth: accountDetails?.dateOfBirth,
      },
      userId: userId,
      credit: {
        id: this.paymentDetailsSelected,
        number: this.newCreditCardDetails.number,
        expDate: this.isNewCardPaymentOptionSelected
          ? dateToIsoString(this.newCreditCardDetails.expDate)
          : this.newCreditCardDetails.expDate,
        name: this.newCreditCardDetails.name,
        cvv: this.newCreditCardDetails.scv,
        isSaved: this.saveNewCard,
        cardType: this.newCreditCardDetails.cardType
      },
      shippingAddressId: checkoutAddress[0].shippingAddress.id,
      billingAddressId: checkoutAddress[0].billingAddress.id,
    };

    _.forOwn(this.storeProductsCheckout, (store, keyStore) => {
      checkoutOrderPayload.orders.push({
        shop: store.storeId,
        products: store.storeProducts.map((product) => {
          return {
            id: product.id,
            shippingAddress: checkoutAddress[0].shippingAddress,
            billingAddress: checkoutAddress[0].billingAddress,
          };
        }),
      });
    });

    try {
      await this.storeApi.ordersCheckout(checkoutOrderPayload)
    } catch (error) {
        const msg = JSON.parse(error.message).message
        this.rootStore.generalStore.showError(msg);
        return;
    }

    this.paymentDetailsSelected = null;
    showSuccess("Payment successful");
    this.getCartByUserId(userId);
    push("/stores");
  };

  private setAllCheckedValues = (
    storeName: string,
    checkedValue: boolean
  ): number[] => {
    let checkedIds = [];
    this.storesProducts[storeName].forEach((product) => {
      product.checked = checkedValue;
      checkedIds.push(product?.id);
    });
    return checkedIds;
  };
}
